import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import ServiceStructuredData from "@/components/ServiceStructuredData";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, Settings, CheckCircle2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import heroImage from "@/assets/heroes/volet-roulant-hero.jpg";
import gallery1 from "@/assets/gallery/volet-roulant-1.jpg";
import gallery2 from "@/assets/gallery/volet-roulant-2.jpg";
import gallery3 from "@/assets/gallery/volet-roulant-3.jpg";

const VoletRoulant = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Volet Roulant Villeurbanne | Dépannage & Installation Rapide"
        description="Dépannage et installation de volets roulants à Villeurbanne : manuel, électrique, motorisation. Intervention rapide 24h/7j par un professionnel qualifié."
        keywords="volet roulant Villeurbanne, dépannage volet roulant Lyon, installation volet électrique, motorisation volet 69"
        canonical="https://www.tolstoi-serrurier.fr/volet-roulant"
        ogUrl="https://www.tolstoi-serrurier.fr/volet-roulant"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <ServiceStructuredData 
        serviceName="Dépannage volet roulant"
        serviceDescription="Dépannage, installation et motorisation de volets roulants à Villeurbanne. Service rapide 24h/7j pour volets manuels et électriques."
        serviceUrl="https://www.tolstoi-serrurier.fr/volet-roulant"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Volet roulant", href: "/volet-roulant" }
        ]} />
      </div>
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative bg-secondary text-secondary-foreground py-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt="Serrurier Villeurbanne - Installation et réparation volet roulant"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 to-secondary/70" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Volet Roulant à Villeurbanne
              </h1>
              <p className="text-xl mb-8 text-secondary-foreground/90">
                Dépannage, installation et motorisation de volets roulants manuels et électriques
              </p>
              <Button size="lg" variant="default" className="gap-2" asChild>
                <a href="tel:0482531608">
                  <Phone className="h-5 w-5" />
                  Appeler maintenant : 04 82 53 16 08
                </a>
              </Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Volet roulant à Villeurbanne — dépannage, installation et motorisation">
          <p>
            Dépannage de <strong>volets roulants manuels et électriques</strong> (sangle cassée, moteur HS, tablier bloqué), installation neuve et <strong>motorisation</strong>. Intervention rapide à Villeurbanne et dans le Grand Lyon.
          </p>
          <p>
            Nous optimisons confort, sécurité et isolation, avec solutions <strong>domotiques</strong> (télécommande, scénarios). En cas d'urgence, voyez notre page <Link to="/depannage-urgence" className="text-primary hover:underline">dépannage 24/7</Link>. Nous intervenons aussi pour{" "}
            <Link to="/ouverture-porte" className="text-primary hover:underline">ouverture de porte</Link> et{" "}
            <Link to="/reparation-serrure" className="text-primary hover:underline">réparation de serrure</Link>.
          </p>
          <p>
            Couverture Lyon 1–9, Bron, Vaulx, Caluire, Meyzieu… <Link to="/zones-intervention" className="text-primary hover:underline">Voir la carte des zones</Link>.
          </p>
        </SEOSubHero>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none mb-12">
                <h2 className="text-3xl font-bold mb-6">Spécialiste volets roulants</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Tolstoï Serrurier intervient à Villeurbanne et dans toute la métropole lyonnaise pour le dépannage, 
                  l'installation et la motorisation de vos volets roulants. Que vous ayez un volet manuel ou électrique, 
                  nous disposons de l'expertise et des pièces nécessaires pour une intervention rapide et efficace.
                </p>
                
                <h3 className="text-2xl font-semibold mb-4 mt-8">Nos services volets roulants</h3>
                <p className="text-muted-foreground mb-6">
                  Du simple dépannage à l'installation complète, en passant par la motorisation de volets manuels, 
                  nous vous proposons une gamme complète de services adaptés à tous vos besoins.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card>
                  <CardContent className="pt-6">
                    <Settings className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Dépannage</h3>
                    <p className="text-sm text-muted-foreground">
                      Réparation de volet bloqué, remplacement de tablier, manivelle, sangle ou moteur défectueux.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Installation</h3>
                    <p className="text-sm text-muted-foreground">
                      Pose de volets roulants neufs, manuels ou électriques, avec coffre intérieur ou extérieur.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Motorisation</h3>
                    <p className="text-sm text-muted-foreground">
                      Transformation de votre volet manuel en volet électrique avec télécommande ou interrupteur.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-muted/50 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Pannes courantes de volets roulants</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Volet bloqué en position haute, basse ou intermédiaire</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Sangle cassée ou manivelle qui tourne dans le vide</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Moteur électrique qui ne fonctionne plus ou fait du bruit</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Tablier endommagé (lames cassées ou sorties des coulisses)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Axe d'enroulement tordu ou cassé</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Télécommande qui ne répond plus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Volet qui descend de travers ou se coince dans les coulisses</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary/10 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Avantages de la motorisation</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Confort d'utilisation</h4>
                    <p className="text-sm text-muted-foreground">
                      Plus besoin de manipuler manivelle ou sangle, commande à distance
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Sécurité renforcée</h4>
                    <p className="text-sm text-muted-foreground">
                      Protection anti-intrusion et anti-soulèvement
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Économies d'énergie</h4>
                    <p className="text-sm text-muted-foreground">
                      Meilleure isolation thermique et acoustique
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Domotique</h4>
                    <p className="text-sm text-muted-foreground">
                      Possibilité de programmation horaire et contrôle à distance
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Besoin d'un dépannage volet roulant ?</h3>
                <p className="text-lg mb-6 text-secondary-foreground/90">
                  Contactez-nous pour une intervention rapide à Villeurbanne et alentours
                </p>
                <Button size="lg" variant="default" className="gap-2" asChild>
                  <a href="tel:0482531608">
                    <Phone className="h-5 w-5" />
                    04 82 53 16 08
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Photo Gallery Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Nos Interventions en Images
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Découvrez la qualité de notre travail à travers nos réalisations
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery1}
                    alt="Volet roulant bloqué endommagé nécessitant réparation Villeurbanne"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Panne</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery2}
                    alt="Technicien réparant moteur volet roulant Lyon"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Réparation</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={gallery3}
                    alt="Volet roulant réparé fonctionnel avec télécommande Villeurbanne"
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-semibold">Résultat</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <CallButton />
    </div>
  );
};

export default VoletRoulant;